/**
 * Request and Response Interceptors
 * Allows middleware-like functionality for API requests
 */

/**
 * Request interceptor function type
 * @typedef {function(Object, Object): Promise<Object>|Object} RequestInterceptor
 */

/**
 * Response interceptor function type
 * @typedef {function(Response, Object): Promise<Response>|Response} ResponseInterceptor
 */

/**
 * Error interceptor function type
 * @typedef {function(Error, Object): Promise<Error>|Error} ErrorInterceptor
 */

class InterceptorManager {
  constructor() {
    this.requestInterceptors = [];
    this.responseInterceptors = [];
    this.errorInterceptors = [];
  }

  /**
   * Add request interceptor
   * @param {RequestInterceptor} interceptor - Interceptor function
   * @returns {function} Unregister function
   */
  addRequestInterceptor(interceptor) {
    this.requestInterceptors.push(interceptor);
    return () => {
      this.requestInterceptors = this.requestInterceptors.filter(i => i !== interceptor);
    };
  }

  /**
   * Add response interceptor
   * @param {ResponseInterceptor} interceptor - Interceptor function
   * @returns {function} Unregister function
   */
  addResponseInterceptor(interceptor) {
    this.responseInterceptors.push(interceptor);
    return () => {
      this.responseInterceptors = this.responseInterceptors.filter(i => i !== interceptor);
    };
  }

  /**
   * Add error interceptor
   * @param {ErrorInterceptor} interceptor - Interceptor function
   * @returns {function} Unregister function
   */
  addErrorInterceptor(interceptor) {
    this.errorInterceptors.push(interceptor);
    return () => {
      this.errorInterceptors = this.errorInterceptors.filter(i => i !== interceptor);
    };
  }

  /**
   * Execute all request interceptors
   * @param {Object} config - Request configuration
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Modified configuration
   */
  async executeRequestInterceptors(config, options = {}) {
    let modifiedConfig = { ...config };
    
    for (const interceptor of this.requestInterceptors) {
      const result = await interceptor(modifiedConfig, options);
      if (result) {
        modifiedConfig = result;
      }
    }
    
    return modifiedConfig;
  }

  /**
   * Execute all response interceptors
   * @param {Response} response - Response object
   * @param {Object} options - Additional options
   * @returns {Promise<Response>} Modified response
   */
  async executeResponseInterceptors(response, options = {}) {
    let modifiedResponse = response;
    
    for (const interceptor of this.responseInterceptors) {
      const result = await interceptor(modifiedResponse, options);
      if (result) {
        modifiedResponse = result;
      }
    }
    
    return modifiedResponse;
  }

  /**
   * Execute all error interceptors
   * @param {Error} error - Error object
   * @param {Object} options - Additional options
   * @returns {Promise<Error>} Modified error
   */
  async executeErrorInterceptors(error, options = {}) {
    let modifiedError = error;
    
    for (const interceptor of this.errorInterceptors) {
      const result = await interceptor(modifiedError, options);
      if (result) {
        modifiedError = result;
      }
    }
    
    return modifiedError;
  }

  /**
   * Clear all interceptors
   */
  clear() {
    this.requestInterceptors = [];
    this.responseInterceptors = [];
    this.errorInterceptors = [];
  }
}

// Export singleton instance
export const interceptors = new InterceptorManager();

// Export class for custom instances
export { InterceptorManager };
